import React from 'react'
import {
  getOutletFormattedAddress,
  getOutletDeliveryFee,
} from '@ancon/wildcat-utils/outlet'
import { getFormattedCurrency } from '@ancon/wildcat-utils/currency'
import OutletBannerPlaceholder from '@ancon/wildcat-ui/shared/static/placeholders/outlet-top-banner.svg'
import { MediaEntityType, OrderFormat } from '@ancon/wildcat-types'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'

import useAppSelector from '../../../store/hooks/useAppSelector'
import {
  outletFiltersOrderFormatSelector,
  outletListItemAvailableOrderFormatsSelector,
  outletListItemSelector,
} from '../store/outletSelector'
import HeadingText from '../../app/components/HeadingText'
import BodyText from '../../app/components/BodyText'
import OrderTypeIcon from '../../app/components/OrderTypeIcon'
import isAppWhiteLabeled from '../../app/utils/isAppWhiteLabeled'
import isOutletDeliveryFeeShown from '../utils/isOutletDeliveryFeeShown'
import OptimizedImage from '../../app/components/OptmizedImage'
import isOutletAvailableForOrders from '../utils/isOutletAvailableForOrders'
import getOutletStatusLabel from '../utils/getOutletStatusLabel'
import getWhiteLabelClassName from '../../app/utils/getWhiteLabelClassName'
import { WhiteLabeledAppName } from '../../app/types'

import OutletOrderPreparationTimeTag from './OutletOrderPreparationTimeTag'
import OutletMoreInfoButton from './OutletMoreInfoButton'
import OutletPurchaseGiftCardButton from './OutletPurchaseGiftCardButton'
import styles from './OutletDetailsHeader.module.scss'

type OutletHeaderOrderFormatsProps = {
  marginBottom?: boolean
}

function OutletHeaderOrderFormats({
  marginBottom,
}: OutletHeaderOrderFormatsProps) {
  const isWhiteLabeled = isAppWhiteLabeled()

  const orderFormats = useAppSelector(
    outletListItemAvailableOrderFormatsSelector,
  )

  return (
    <div
      className={clsx(styles.orderFormats, {
        [styles.marginBottom]: marginBottom,
        [styles.whiteLabeled]: isWhiteLabeled,
      })}
    >
      {orderFormats.map(format => (
        <OrderTypeIcon key={format} orderFormat={format} size={48} />
      ))}
    </div>
  )
}

export default function OutletDetailsHeader() {
  const isWhiteLabeled = isAppWhiteLabeled()

  const outlet = useAppSelector(outletListItemSelector)

  const { t } = useTranslation('outlet')

  const { name, address, imageId, logoId, serviceFees, tenantId } = outlet!

  const deliveryFee = getOutletDeliveryFee(serviceFees)

  const isAvailableForOrders = isOutletAvailableForOrders(outlet)

  const orderFormat = useAppSelector(outletFiltersOrderFormatSelector)

  const showDeliveryFee = isOutletDeliveryFeeShown(outlet!)

  const outletStatusLabel = getOutletStatusLabel(outlet, t)

  return (
    <div
      className={clsx(styles.container, {
        [styles.whiteLabeled]: isWhiteLabeled,
      })}
    >
      {!isWhiteLabeled && (
        <div className={styles.imgContainer}>
          <OptimizedImage
            tenantId={tenantId}
            mediaId={imageId}
            mediaEntityId={outlet?.id!}
            mediaEntityType={MediaEntityType.Outlet}
            src={OutletBannerPlaceholder}
            alt={`${name} banner`}
            className={styles.bannerImage}
            height={186}
            width={280}
            priority
          />
          <OptimizedImage
            tenantId={tenantId}
            mediaId={logoId}
            mediaEntityId={outlet?.id!}
            mediaEntityType={MediaEntityType.Outlet}
            src="/images/outlet-logo-placeholder.jpg"
            alt={`${name} logo`}
            className={styles.logoImage}
            height={100}
            width={100}
            priority
          />
        </div>
      )}
      <div className={styles.detailsContainer}>
        <div>
          <div className={styles.headerWrapper}>
            <HeadingText as="h2" title={name}>
              {name}
            </HeadingText>

            {isWhiteLabeled && (
              <div className={styles.rightActions}>
                <OutletPurchaseGiftCardButton />
                <OutletMoreInfoButton />
              </div>
            )}
          </div>
          <BodyText
            fontSize="1.6rem"
            color="body-1"
            title={getOutletFormattedAddress(address)}
            className={clsx(
              styles.outletAddress,
              getWhiteLabelClassName({
                [WhiteLabeledAppName.Brutale]: styles.brutaleApp,
              }),
            )}
          >
            {getOutletFormattedAddress(address)}
          </BodyText>
          <div className={styles.outletStateInfo}>
            <div className={styles.deliveryStatusRow}>
              <BodyText
                className={clsx({
                  [styles.dangerText]: !isAvailableForOrders,
                  [styles.successText]: isAvailableForOrders,
                })}
              >
                {outletStatusLabel}
              </BodyText>
              {showDeliveryFee && (
                <BodyText className={styles.deliveryInfoText} color="body-1">
                  {deliveryFee?.amount
                    ? t('deliveryFee', {
                        amount: getFormattedCurrency(
                          deliveryFee.amount.amount,
                          deliveryFee.amount.currency,
                        ),
                      })
                    : t('freeDelivery')}
                </BodyText>
              )}
              {isWhiteLabeled && <OutletHeaderOrderFormats />}
            </div>

            {orderFormat !== OrderFormat.TableOrder && (
              <OutletOrderPreparationTimeTag
                outlet={outlet}
                orderFormat={orderFormat}
              />
            )}
          </div>
        </div>

        {!isWhiteLabeled && (
          <div className={styles.detailsBottomRow}>
            <OutletHeaderOrderFormats marginBottom />

            <div className={styles.outletActionsContainer}>
              <OutletMoreInfoButton />
              <OutletPurchaseGiftCardButton />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
